import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  commitFolder,
  getFolderBatchJob,
  previewFolder,
  saveFolderMappings,
  type FolderBatchStatus,
  type FolderPreviewSheet,
  type FolderSheetPlan,
  type PreviewStatement,
} from '@/lib/api';
import { createClass, useOntologySchema } from '@/lib/useOntology';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import {
  acceptSuggestions,
  columnKind,
  emptyMapping,
  currentDraft,
  draftStarted,
  EMPTY_DRAFT,
  mappingProblems,
  mappingsToColumns,
  mappingsToNewClasses,
  specToMappings,
  suggestedMappings,
  type ColumnMapping,
} from '@/lib/columnMapping';
import { folderProblems, linkProblems, lowerSheets, sheetKey, sheetName, sheetOf, type FolderLink, type SheetState } from '@/lib/folderMapping';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { describeApiError } from '@/lib/apiErrors';
import { ColumnsPage } from '@/components/import/ColumnsPage';
import { LinksPage } from '@/components/import/LinksPage';
import { PropertiesPage } from '@/components/import/PropertiesPage';
import { RulesPage } from '@/components/import/RulesPage';
import { SheetPanel } from '@/components/import/SheetPanel';
import { ValidationStep } from '@/components/import/ValidationStep';
import { Message } from '@/components/ui/Message';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

/**
 * Several sheets through the pages (ruled 2026-09-30, 4.11): one Columns
 * page over every sheet, the links between sheets, then the properties, the
 * rules, Validate per sheet and one Commit. The folder is staged by the
 * folder import and its plan carries each sheet's first rows, profile and
 * proposed mapping, so the pages read nothing again; Validate goes to the
 * folder preview, which plans each sheet after the ones before it, so what
 * it shows is what the commit writes.
 */

type Page = 'columns' | 'links' | 'properties' | 'rules' | 'validate' | 'done';
const PAGES: { id: Page; label: string }[] = [
  { id: 'columns', label: 'Columns' },
  { id: 'links', label: 'Links' },
  { id: 'properties', label: 'Properties' },
  { id: 'rules', label: 'Rules' },
  { id: 'validate', label: 'Validate' },
  { id: 'done', label: 'Done' },
];

const ONTOLOGY_GRAPH = 'urn:tres:ontology';
const DATA_GRAPH = 'urn:tres:data';
const btnPrimary = 'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const btnGhost = 'rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50';

interface SheetPreview {
  preview: FolderPreviewSheet;
  leftOut: PreviewStatement[];
}

/** One sheet's state from its plan: the rows and profile the plan carries.
 *  A sheet whose header set has a saved mapping opens with that mapping,
 *  every column it knows answered and any other column unanswered with the
 *  proposal's suggestion; a sheet with none opens with a suggestion for each
 *  column from the plan's proposed mapping. */
function sheetFromPlan(p: FolderSheetPlan): SheetState {
  const proposal = p.spec ?? { namespace: 'resource:', columns: [] };
  const suggestions = specToMappings(proposal);
  const saved = p.saved_mapping;
  const base = saved ? saved.spec : proposal;
  const hints = new Map(suggestions.map((m) => [m.column, m]));
  const mappings = saved
    ? specToMappings(saved.spec).map((m) =>
        saved.extra_columns.includes(m.column) ? { ...emptyMapping(m.column), unanswered: true, suggestion: hints.get(m.column) } : m,
      )
    : suggestedMappings(suggestions);
  return {
    file: p.sheet.file,
    sheet: p.sheet.sheet,
    headerRow: p.header_row,
    columns: p.columns,
    rows: p.first_rows,
    profiles: new Map(p.profile.columns.map((c) => [c.name, c])),
    dependencies: p.profile.dependencies,
    mappings,
    spec: { ...base, columns: mappingsToColumns(mappings, base.columns) },
    draft: EMPTY_DRAFT,
    savedMapping: saved ? { id: saved.id, name: saved.name } : undefined,
    earlier: p.known?.state === 'new_version' ? p.known.loads : undefined,
    version: p.known?.state === 'new_version' ? 'add' : undefined,
  };
}

export function FolderMappingPanel({
  sessionId,
  plans,
  suggestedLinks,
  savedLinks,
  token,
  onBack,
}: {
  sessionId: string;
  /** The sheets the folder plan admits, in its order. */
  plans: FolderSheetPlan[];
  /** The rows the assist report suggests for the links page, if it ran. */
  suggestedLinks?: FolderLink[];
  /** The links saved between these sheets' header sets, already accepted. */
  savedLinks?: FolderLink[];
  token: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const ontologySchema = useOntologySchema(ALL_GRAPHS, token);
  const [page, setPage] = useState<Page>('columns');
  const [sheets, setSheets] = useState<SheetState[]>(() => plans.map(sheetFromPlan));
  const [links, setLinks] = useState<FolderLink[]>([...(savedLinks ?? []), ...(suggestedLinks ?? [])]);
  const [saveNote, setSaveNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveOnImport, setSaveOnImport] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ sheet: string; columns: string[] }[]>([]);
  const [previews, setPreviews] = useState<Map<string, SheetPreview>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [batch, setBatch] = useState<FolderBatchStatus | null>(null);
  const [targetGraph, setTargetGraph] = useState(DATA_GRAPH);
  const rowsOf = (s: SheetState) => plans.find((p) => p.sheet.file === s.file && p.sheet.sheet === s.sheet)?.rows ?? s.rows.length;

  const live = useMemo(() => sheets.filter((s) => !s.leftOut), [sheets]);
  const update = (key: string, change: (s: SheetState) => SheetState) =>
    setSheets((all) => all.map((s) => (sheetKey(s) === key ? change(s) : s)));
  const withMappings = (s: SheetState, next: ColumnMapping[]): SheetState => ({
    ...s,
    mappings: next,
    spec: { ...s.spec, columns: mappingsToColumns(next, s.spec.columns), new_classes: mappingsToNewClasses(next) },
  });
  const setMappings = (key: string, next: ColumnMapping[]) => update(key, (s) => withMappings(s, next));
  const waiting = live.reduce((n, s) => n + s.mappings.filter((m) => m.unanswered && m.suggestion).length, 0);
  const waitingSheets = live.filter((s) => s.mappings.some((m) => m.unanswered && m.suggestion)).length;
  const acceptEverySheet = () => setSheets((all) => all.map((s) => (s.leftOut ? s : withMappings(s, acceptSuggestions(s.mappings)))));

  async function declareClass(c: { label: string; parent: string; uri: string }) {
    const r = await createClass({ graph: ONTOLOGY_GRAPH, label: c.label, super_classes: c.parent ? [c.parent] : [], uri: c.uri }, token);
    if (!r.ok) return { ok: false as const, error: r.message };
    await queryClient.invalidateQueries({ queryKey: ['ontology'] });
    return { ok: true as const, uri: r.data.uri };
  }

  const problems = useMemo(() => folderProblems(live, links), [live, links]);
  const columnsSettled = live.every((s) => !mappingProblems(s.mappings).some((p) => p.field === 'header' || p.field === 'class'));
  const anyDraft = live.some((s) => draftStarted(currentDraft(s.draft, s.mappings)));
  const hasRules = live.some((s) => s.mappings.some((m) => columnKind(m) === 'rule' && m.job === 'code'));
  const mappingValid = problems.length === 0 && !anyDraft;
  const pages = PAGES.filter((p) => p.id !== 'rules' || hasRules);
  const lowered = useMemo(() => lowerSheets(live, links), [live, links]);
  const highlightFor = (key: string) => new Set(focus.filter((f) => f.sheet === key).flatMap((f) => f.columns));

  // The whole set in one call, each sheet planned over the staged file after
  // the ones before it. The commit sends the same sheets, specs and header
  // rows, so what Validate shows is what the commit writes.
  async function goValidate() {
    setBusy('preview');
    setError(null);
    const r = await previewFolder(
      sessionId,
      {
        sheets: lowered.map(({ sheet, spec }) => ({ relative: sheet.file, sheet: sheet.sheet, spec, header_row: sheet.headerRow })),
        links: links.filter((l) => l.accepted),
        decisions: {},
      },
      token,
    );
    if (!r.ok) {
      setError(`The statements could not be built: ${r.message}`);
      setBusy(null);
      return;
    }
    const out = new Map<string, SheetPreview>();
    for (const p of r.data.sheets) out.set(sheetKey(p.sheet), { preview: p, leftOut: [] });
    if (r.data.failed.length > 0) {
      setError(
        `Not validated: ${r.data.failed.map((f) => `${sheetName(f.sheet)} (${f.error})`).join('; ')}. The import writes every sheet, so fix these before importing.`,
      );
    }
    setPreviews(out);
    setBusy(null);
    setPage('validate');
  }

  // Every sheet's mapping saved by its header set, and the links between
  // the sheets, so the next upload of a sheet opens with these answers. The
  // mapping saved is the sheet's own, without the links page lowered into
  // it; the links are saved beside it. Answers with each sheet's saved
  // mapping, or null when nothing was saved.
  async function saveMappings(): Promise<Map<string, { id: string; name: string }> | null> {
    const waitingOn = problems[0];
    if (waitingOn || anyDraft) {
      const where = waitingOn?.sheet ? `${sheetName(sheetOf(live, waitingOn.sheet) ?? { file: waitingOn.sheet, sheet: 'Sheet1' })}: ` : '';
      setSaveNote({
        ok: false,
        text: `Not saved. ${waitingOn ? `${where}${waitingOn.problem.text}` : 'A link in an add row is not added yet.'} A mapping is saved once every column is answered.`,
      });
      return null;
    }
    setBusy('save');
    const place = (key: string, column: string) => {
      const s = sheetOf(live, key);
      return s ? { relative: s.file, sheet: s.sheet, column } : null;
    };
    const r = await saveFolderMappings(
      sessionId,
      {
        sheets: live.map((s) => ({
          relative: s.file,
          sheet: s.sheet,
          columns: s.columns,
          spec: { ...s.spec, columns: mappingsToColumns(s.mappings, s.spec.columns), new_classes: mappingsToNewClasses(s.mappings) },
        })),
        links: links
          .filter((l) => l.accepted && linkProblems(l, live).length === 0)
          .flatMap((l) => {
            const from = place(l.from.sheet, l.from.column);
            const to = place(l.to.sheet, l.to.column);
            return from && to
              ? [{ kind: l.kind, from, to, ...(l.predicate ? { predicate: l.predicate } : {}), ...(l.primary ? { primary: l.primary } : {}), ...(l.via ? { via: l.via } : {}) }]
              : [];
          }),
      },
      token,
    );
    setBusy(null);
    if (!r.ok) {
      setSaveNote({ ok: false, text: `Not saved: ${r.message}` });
      return null;
    }
    const byKey = new Map(r.data.saved.map((m) => [sheetKey({ file: m.relative, sheet: m.sheet }), { id: m.id, name: m.name }]));
    setSheets((all) => all.map((s) => (byKey.has(sheetKey(s)) ? { ...s, savedMapping: byKey.get(sheetKey(s)) } : s)));
    const created = r.data.saved.filter((m) => m.created).length;
    const replaced = r.data.saved.length - created;
    setSaveNote({
      ok: true,
      text: `Saved ${r.data.saved.length} mapping${r.data.saved.length === 1 ? '' : 's'} (${created} new, ${replaced} replaced)${
        r.data.links > 0 ? ` and ${r.data.links} link${r.data.links === 1 ? '' : 's'} between sheets` : ''
      }. A sheet with the same columns, or those and more, opens with its mapping the next time it is uploaded.`,
    });
    return byKey;
  }

  const saveControls = (
    <>
      <Button type="button" variant="secondary" size="md" disabled={busy !== null} onClick={() => void saveMappings()}>
        {busy === 'save' ? 'Saving…' : 'Save mappings'}
      </Button>
      {saveNote && (
        <Message kind={saveNote.ok ? 'outcome' : 'warning'} density="compact">
          {saveNote.text}
        </Message>
      )}
    </>
  );

  async function doCommit() {
    setError(null);
    const savedNow = saveOnImport ? await saveMappings() : null;
    setBusy('commit');
    const r = await commitFolder(
      sessionId,
      [],
      lowered.map(({ sheet, spec }) => {
        const mapping = savedNow?.get(sheetKey(sheet)) ?? sheet.savedMapping;
        return {
          relative: sheet.file,
          sheet: sheet.sheet,
          spec,
          header_row: sheet.headerRow,
          graph: targetGraph,
          mode: 'append' as const,
          ...(mapping ? { mapping_id: mapping.id, mapping_name: mapping.name } : {}),
          ...(sheet.version === 'replace' ? { replace_earlier: true } : {}),
        };
      }),
      token,
    );
    if (!r.ok) {
      setError(`The import was not started: ${r.message}`);
      setBusy(null);
      return;
    }
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const st = await getFolderBatchJob(r.data.job_id, token);
      if (!st.ok) {
        setError(`The import's progress could not be read: ${st.message}`);
        break;
      }
      setBatch(st.data);
      if (st.data.state !== 'running') break;
    }
    // The store changed: every cached count, list and index reading is stale.
    invalidateAfterDataWrite(queryClient);
    setBusy(null);
    setPage('done');
  }

  return (
    <div className="space-y-4">
      <ol className="flex flex-wrap items-center gap-2 text-xs">
        {pages.map((p, i) => {
          const active = p.id === page;
          const done = pages.findIndex((x) => x.id === page) > i;
          return (
            <li key={p.id} className="flex items-center gap-1">
              <span className={cn('rounded-full px-2.5 py-0.5 font-medium', active && 'bg-primary text-primary-foreground', done && !active && 'bg-secondary text-secondary-foreground', !active && !done && 'bg-muted text-muted-foreground')}>
                {i + 1}. {p.label}
              </span>
              {i < pages.length - 1 && <span className="text-muted-foreground">→</span>}
            </li>
          );
        })}
      </ol>

      {sheets.some((s) => s.leftOut) && (
        <Message kind="platform" tone="warning" density="compact">
          Left out: {sheets.filter((s) => s.leftOut).map((s) => `${sheetName(s)} (${s.leftOut})`).join('; ')}.
        </Message>
      )}

      {page === 'columns' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold">Page 1 — Columns, every sheet</h3>
            <p className="text-xs text-muted-foreground">
              {live.length} sheet{live.length === 1 ? '' : 's'}. Each below with its own header row and first two rows;
              say what each column is, sheet by sheet.
            </p>
          </div>
          {waiting > 0 && (
            <div className="space-y-2">
              <Message kind="guidance" density="compact">
                {waiting} column{waiting === 1 ? '' : 's'} on {waitingSheets} sheet{waitingSheets === 1 ? '' : 's'} wait
                {waiting === 1 ? 's' : ''} on an answer. Each has a suggestion read from the sheets themselves: look them
                over below, then accept them all at once or answer sheet by sheet.
              </Message>
              <Button type="button" variant="secondary" size="sm" onClick={acceptEverySheet}>
                Accept all suggestions on every sheet
              </Button>
            </div>
          )}
          {live.map((s) => (
            <section key={sheetKey(s)} aria-label={`Sheet ${sheetName(s)}`} className="space-y-2 rounded-md border border-border p-3">
              <h4 className="text-sm font-semibold">
                {sheetName(s)}
                <span className="ml-2 font-normal text-muted-foreground">
                  {rowsOf(s).toLocaleString()} rows; the first {Math.min(s.rows.length, rowsOf(s))} shown
                </span>
                <span className="ml-2 font-normal text-muted-foreground">
                  {s.savedMapping ? `Answers from the saved mapping ${s.savedMapping.name}.` : 'Answers suggested from the sheets.'}
                </span>
              </h4>
              <SheetPanel columns={s.columns} rows={s.rows} highlight={highlightFor(sheetKey(s))} />
              <ColumnsPage
                mappings={s.mappings}
                profiles={s.profiles}
                schema={ontologySchema.data ?? null}
                onFocus={(columns) => setFocus([{ sheet: sheetKey(s), columns }])}
                onCreateClass={declareClass}
                heading={false}
                onChange={(next) => setMappings(sheetKey(s), next)}
              />
            </section>
          ))}
          <div className="flex items-center gap-3">
            <button type="button" onClick={onBack} className={btnGhost}>
              Back to the folder
            </button>
            <button type="button" onClick={() => setPage('links')} disabled={!columnsSettled} className={btnPrimary}>
              Next: the links between sheets →
            </button>
            {!columnsSettled && <span className="text-xs text-muted-foreground">Answer every column of every sheet and choose the class of each thing column to go on.</span>}
          </div>
        </div>
      )}

      {page === 'links' && (
        <div className="space-y-4">
          {[...new Set(focus.map((f) => f.sheet))].slice(0, 2).map((key) => {
            const s = live.find((x) => sheetKey(x) === key);
            return s ? (
              <div key={key}>
                <p className="mb-1 text-xs text-muted-foreground">{sheetName(s)}</p>
                <SheetPanel columns={s.columns} rows={s.rows} highlight={highlightFor(key)} />
              </div>
            ) : null;
          })}
          <LinksPage
            sheets={live}
            schema={ontologySchema.data ?? null}
            links={links}
            onChange={setLinks}
            onFocus={(cols) => setFocus(cols.map((c) => ({ sheet: c.sheet, columns: [c.column] })))}
          />
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setPage('columns')} className={btnGhost}>
              Back
            </button>
            <button type="button" onClick={() => setPage('properties')} className={btnPrimary}>
              Next: the properties →
            </button>
          </div>
        </div>
      )}

      {page === 'properties' && (
        <div className="space-y-6">
          {live.map((s) => (
            <section key={sheetKey(s)} aria-label={`Properties of ${sheetName(s)}`} className="space-y-2 rounded-md border border-border p-3">
              <h4 className="text-sm font-semibold">{sheetName(s)}</h4>
              <SheetPanel columns={s.columns} rows={s.rows} highlight={highlightFor(sheetKey(s))} />
              <PropertiesPage
                mappings={s.mappings}
                rows={s.rows}
                dependencies={s.dependencies}
                schema={ontologySchema.data ?? null}
                draft={s.draft}
                onDraft={(draft) => update(sheetKey(s), (x) => ({ ...x, draft }))}
                onFocus={(columns) => setFocus([{ sheet: sheetKey(s), columns }])}
                heading={false}
                onChange={(next) => setMappings(sheetKey(s), next)}
              />
            </section>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setPage('links')} className={btnGhost}>
              Back
            </button>
            {hasRules ? (
              <button type="button" onClick={() => setPage('rules')} disabled={anyDraft} className={btnPrimary}>
                Next: the rules →
              </button>
            ) : (
              <button type="button" onClick={() => void goValidate()} disabled={busy !== null || !mappingValid} className={btnPrimary}>
                {busy === 'preview' ? 'Building statements…' : 'Validate statements →'}
              </button>
            )}
            {saveControls}
            {!anyDraft && problems.length > 0 && (
              <Message kind="warning" density="compact">
                Validate waits on: {[...new Set(problems.map((p) => `${p.sheet ? `${sheetName(live.find((s) => sheetKey(s) === p.sheet) ?? { file: '', sheet: 'Sheet1' })}: ` : ''}${p.problem.text}`))].slice(0, 3).join(' ')}
              </Message>
            )}
            {anyDraft && (
              <Message kind="warning" density="compact">
                A link in an add row is not added yet. Press Add to keep it, or clear the row.
              </Message>
            )}
            {error && (
              <Message kind="warning" density="compact">
                {error}
              </Message>
            )}
          </div>
        </div>
      )}

      {page === 'rules' && (
        <div className="space-y-6">
          {live
            .filter((s) => s.mappings.some((m) => columnKind(m) === 'rule' && m.job === 'code'))
            .map((s) => (
              <section key={sheetKey(s)} aria-label={`Rules of ${sheetName(s)}`} className="space-y-2 rounded-md border border-border p-3">
                <h4 className="text-sm font-semibold">{sheetName(s)}</h4>
                <SheetPanel columns={s.columns} rows={s.rows} highlight={highlightFor(sheetKey(s))} />
                <RulesPage
                  mappings={s.mappings}
                  rows={s.rows}
                  schema={ontologySchema.data ?? null}
                  onFocus={(columns) => setFocus([{ sheet: sheetKey(s), columns }])}
                  heading={false}
                  onChange={(next) => setMappings(sheetKey(s), next)}
                />
              </section>
            ))}
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setPage('properties')} className={btnGhost}>
              Back
            </button>
            <button type="button" onClick={() => void goValidate()} disabled={busy !== null || !mappingValid} className={btnPrimary}>
              {busy === 'preview' ? 'Building statements…' : 'Validate statements →'}
            </button>
            {problems.length > 0 && (
              <Message kind="warning" density="compact">
                Validate waits on: {[...new Set(problems.map((p) => p.problem.text))].slice(0, 3).join(' ')}
              </Message>
            )}
            {error && (
              <Message kind="warning" density="compact">
                {error}
              </Message>
            )}
          </div>
        </div>
      )}

      {page === 'validate' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold">Validation — the statements each sheet will store</h3>
            <p className="text-xs text-muted-foreground">
              Each sheet's statements, each beside the cell it was read from. Everything is written in one import,
              schema first, sheet by sheet. Nothing is written until you import.
            </p>
          </div>
          {error && (
            <Message kind="warning" density="compact">
              {error}
            </Message>
          )}
          {lowered.map(({ sheet }) => {
            const key = sheetKey(sheet);
            const p = previews.get(key);
            if (!p) return null;
            const columnOfPredicate = new Map(sheet.mappings.filter((m) => m.predicate.trim()).map((m) => [m.predicate.trim(), m.column]));
            const c = p.preview.counts;
            return (
              <section key={key} aria-label={`Validation of ${sheetName(sheet)}`} className="space-y-2 rounded-md border border-border p-3">
                <h4 className="text-sm font-semibold">{sheetName(sheet)}</h4>
                <p className="text-xs text-muted-foreground">
                  {c.rows_in.toLocaleString()} rows read{c.rows_skipped > 0 ? `, ${c.rows_skipped.toLocaleString()} with no subject` : ''};{' '}
                  {c.entities_minted.toLocaleString()} new things, {c.entities_reconciled.toLocaleString()} matched in the store
                  {c.joined > 0 ? `, ${c.joined.toLocaleString()} found by an identifier in the store or an earlier sheet` : ''};{' '}
                  {c.statements.toLocaleString()} statements
                  {c.references_dropped > 0 ? `; ${c.references_dropped.toLocaleString()} references to nothing dropped` : ''}.
                </p>
                {sheet.earlier && sheet.earlier[0] && (
                  <fieldset aria-label={`Which version of ${sheetName(sheet)} to trust`} className="space-y-1 rounded-md border border-border p-2 text-sm">
                    <legend className="px-1 text-xs text-muted-foreground">
                      A new version of the sheet loaded on {new Date(sheet.earlier[0].imported_at).toLocaleString()}
                      {sheet.earlier[0].rows != null ? `, which had ${sheet.earlier[0].rows.toLocaleString()} rows` : ''}
                      {sheet.earlier.length > 1 ? `, and ${sheet.earlier.length - 1} before it` : ''}.
                    </legend>
                    <label className="flex items-start gap-2">
                      <input type="radio" name={`version-${key}`} checked={sheet.version !== 'replace'} onChange={() => update(key, (s) => ({ ...s, version: 'add' }))} />
                      <span>
                        Add, keep both. What both versions state is kept once; what only the earlier one stated stays
                        until that load is removed in the Catalog.
                      </span>
                    </label>
                    <label className="flex items-start gap-2">
                      <input type="radio" name={`version-${key}`} checked={sheet.version === 'replace'} onChange={() => update(key, (s) => ({ ...s, version: 'replace' }))} />
                      <span>
                        Replace the earlier version. After this one is imported, what only the earlier one stated is
                        removed.
                      </span>
                    </label>
                  </fieldset>
                )}
                <SheetPanel columns={sheet.columns} rows={sheet.rows} highlight={new Set()} />
                <ValidationStep
                  preview={p.preview}
                  rows={sheet.rows}
                  reconcileItems={[]}
                  decisions={{}}
                  leftOut={p.leftOut}
                  columnOfPredicate={columnOfPredicate}
                  previewOffset={0}
                  busy={busy !== null}
                  onDecide={() => undefined}
                  onLeaveOut={(statements) => setPreviews((m) => new Map(m).set(key, { ...p, leftOut: [...p.leftOut, ...statements] }))}
                  onPutBack={(ids) => setPreviews((m) => new Map(m).set(key, { ...p, leftOut: p.leftOut.filter((s) => !ids.includes(s.id)) }))}
                  onRecheck={() => undefined}
                  onPage={() => undefined}
                />
              </section>
            );
          })}
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="folder-target-graph">
              Target graph
            </label>
            <input
              id="folder-target-graph"
              type="text"
              value={targetGraph}
              onChange={(e) => setTargetGraph(e.target.value)}
              className="block w-80 rounded-md border border-input bg-background px-2 py-1 font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">Data is appended to this graph; classes and properties go to the ontology, vocabulary terms to the vocabulary.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={saveOnImport} onChange={(e) => setSaveOnImport(e.target.checked)} />
            Save these mappings, so the next upload of these sheets opens with them
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setPage(hasRules ? 'rules' : 'properties')} disabled={busy !== null} className={btnGhost}>
              Back
            </button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={busy !== null || !targetGraph.trim() || lowered.some(({ sheet }) => !previews.has(sheetKey(sheet)))}
              onClick={() => void doCommit()}
            >
              {busy === 'commit' ? 'Importing…' : `Import ${lowered.length} sheet${lowered.length === 1 ? '' : 's'} to ${targetGraph}`}
            </Button>
            {saveControls}
          </div>
        </div>
      )}

      {page === 'done' && batch && (
        <div className="space-y-3">
          <Message kind={batch.state === 'done' && !batch.error ? 'outcome' : 'warning'} density="normal">
            {batch.state === 'done'
              ? `Imported ${batch.result?.loaded ?? batch.progress.done} of ${batch.selected} sheets${batch.result?.failed ? `; ${batch.result.failed} failed` : ''}.`
              : `The import ${batch.state}. ${describeApiError(batch.http_status ?? 500, batch.error, batch.detail)}`}
          </Message>
          <table className="tres-table text-xs" aria-label="What each sheet wrote">
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Result</th>
                <th>What was written</th>
              </tr>
            </thead>
            <tbody>
              {(batch.result?.items ?? batch.progress.items).map((it) => (
                <tr key={it.item}>
                  <td>{it.item.replace(/ :: Sheet1$/, '')}</td>
                  <td className={it.ok ? undefined : 'text-tres-error'}>{it.ok ? 'Imported' : 'Not imported'}</td>
                  <td className={it.ok ? undefined : 'text-tres-error'}>{it.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(batch.result?.graphs ?? []).filter((g) => g.before !== g.after).length > 0 && (
            <div className="text-xs text-muted-foreground">
              {(batch.result?.graphs ?? [])
                .filter((g) => g.before !== g.after)
                .map((g) => (
                  <p key={g.graph}>
                    {g.graph}: {g.before == null ? 'unknown' : g.before.toLocaleString()} → {g.after == null ? 'unknown' : g.after.toLocaleString()} statements
                  </p>
                ))}
            </div>
          )}
          <button type="button" onClick={onBack} className={btnGhost}>
            Back to the folder
          </button>
        </div>
      )}
    </div>
  );
}

